import React, { useCallback, useEffect, useRef, useState } from "react";
import { BASE_URL } from "../../../utils/config";
import { adminRequest } from "../../../utils/requestMethods";
import NotFound from "../../../components/NotFound/NotFound";
import "./FacultyList.scss";
import { ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { CiSearch } from "react-icons/ci";
import { IoIosAddCircle, IoIosMore } from "react-icons/io";
import OptionsMenu from "../../../components/OptionsMenu/OptionsMenu";
import NoRecord from "../../../components/NoRecord/NoRecord";
import { IoDownloadOutline } from "react-icons/io5";

const FacultyList = () => {
  const headers = [
    "Name",
    "Faculty Type",
    "Mobile Number",
    "Qualification",
    "Specialization",
    "Hired Date",
    "Status",
  ];
  const [rows, setRows] = useState([]);
  const [visibleMenu, setVisibleMenu] = useState(null);
  const menuRefs = useRef([]);
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleCreateButtonClick = () => {
    navigate("/setup/faculty/create");
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchKeyword(value);
    handleSearch.current(value);
  };

  const handleMoreClick = (rowIndex) => {
    setVisibleMenu(visibleMenu === rowIndex ? null : rowIndex);
  };

  const fetchData = useCallback(async () => {
    const firstRow = (currentPage - 1) * pageSize;
    try {
      const response = await adminRequest.post(`${BASE_URL}/faculty/list`, {
        firstRow,
        pageSize,
        status: statusFilter || "",
      });
      const { data, totalCount } = response.data;
      const fetchedRows = data.map((f) => ({
        displayData: [
          f.name,
          f.facultyCategory.name,
          f.mobileNumber,
          f.qualification,
          f.specialization,
          f.hire_date,
          f.status,
        ],
        uuid: f.uuid,
        status: f.status,
      }));
      setRows(fetchedRows);
      setTotalCount(totalCount);
    } catch (error) {
      return <NotFound />;
    }
  }, [currentPage, pageSize, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData, statusFilter]);

  const handleSearch = React.useRef(null);

  useEffect(() => {
    handleSearch.current = _.debounce(async (query) => {
      if (!query) {
        fetchData();
        return;
      }
      try {
        const response = await adminRequest.post(`${BASE_URL}/faculty/search`, {
          searchKeyword: query,
        });
        const fetchedRows = response.data.data.map((f) => ({
          displayData: [
            f.name,
            f.facultyCategory.name,
            f.mobileNumber,
            f.qualification,
            f.specialization,
            f.hire_date,
            f.status,
          ],
          uuid: f.uuid,
          status: f.status,
        }));
        setRows(fetchedRows);
      } catch (error) {
        console.error(error);
      }
    }, 300);
  }, [fetchData]);

  const setActive = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to activate this faculty?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "SetActive",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/faculty/setActive`,
            {
              uuid: uuid,
            }
          );
          Swal.fire({
            title: "Activated!",
            text: `${response.data.message}`,
            icon: "success",
          });

          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };

  const setLeave = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to set on leave this faculty?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "SetLeave",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/faculty/setLeave`,
            {
              uuid: uuid,
            }
          );

          Swal.fire({
            title: "On Leave!",
            text: `${response.data.message}`,
            icon: "success",
          });
          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };

  const setRetire = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to retire this faculty?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "Retire",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/faculty/retire`,
            {
              uuid: uuid,
            }
          );

          Swal.fire({
            title: "Retired!",
            text: `${response.data.message}`,
            icon: "success",
          });

          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };
  const setTerminate = async (uuid) => {
    Swal.fire({
      title: "Are you sure you want to terminate this faculty?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#264796",
      cancelButtonColor: "#FC0000",
      confirmButtonText: "Terminate",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await adminRequest.post(
            `${BASE_URL}/faculty/terminate`,
            {
              uuid: uuid,
            }
          );

          Swal.fire({
            title: "Terminated!",
            text: `${response.data.message}`,
            icon: "success",
          });

          fetchData();
        } catch (error) {
          Swal.fire({
            title: "Failed!",
            text: `${error.response.data.message}`,
            icon: "error",
          });
        }
      }
    });
  };

  const handleExportToCSV = async () => {
    try {
      const response = await adminRequest.get(`${BASE_URL}/faculty/download`, {
        responseType: "blob",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(response.data);
      link.download = "faculty_list.csv";
      link.click();
    } catch (error) {
      console.error("Error exporting CSV", error);
    }
  };

  const getMenuItems = (row) => [
    { link: `view/${row.uuid}`, text: "View" },
    { link: `edit/${row.uuid}`, text: "Edit" },
    {
      link: "#",
      text: "Retire",
      onClick: (e) => {
        e.preventDefault();
        setRetire(row.uuid);
      },
    },
    {
      link: "#",
      text: "Terminate",
      onClick: (e) => {
        e.preventDefault();
        setTerminate(row.uuid);
      },
    },
    {
      link: "#",
      text: "SetLeave",
      onClick: (e) => {
        e.preventDefault();
        setLeave(row.uuid);
      },
    },
    {
      link: "#",
      text: "SetActive",
      onClick: (e) => {
        e.preventDefault();
        setActive(row.uuid);
      },
    },
  ];

  const startRecord = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="facultyListContainer">
      <div className="facultyListContents">
        <div className="top">
          <span className="leftTitle">
            <FaArrowLeftLong onClick={handleBackClick} className="icon" />
            Faculty List
          </span>
          <span className="rightContents">
            <span className="searchField">
              <input
                type="text"
                name="search"
                placeholder="Search Faculty..."
                className="searchInput"
                value={searchKeyword}
                onChange={handleInputChange}
              />
              <span className="searchIcon">
                <CiSearch />
              </span>
            </span>

            <span className="exportButton" onClick={handleExportToCSV}>
              <span className="title">Download</span>
              <span className="csvIcon">
                <IoDownloadOutline />
              </span>
            </span>
            <span className="createButton" onClick={handleCreateButtonClick}>
              <span className="title">Create Faculty</span>
              <span className="addIcon">
                <IoIosAddCircle />
              </span>
            </span>
          </span>
        </div>
        <div className="bottom">
          <div className="filterContainer">
            <select
              className="statusDropdown"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="RETIRED">RETIRED</option>
              <option value="TERMINATED">TERMINATED</option>
              <option value="LEAVE">ON LEAVE</option>
            </select>
          </div>
          <div className="recordSummary">
            <select
              className="pageSize"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </div>
          <table className="listTable">
            <thead>
              <tr className="tableHeaderRow">
                <th></th>
                <th></th>
                {headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows && rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    <td
                      style={{ position: "relative" }}
                      ref={(el) => (menuRefs.current[rowIndex] = el)}
                    >
                      <IoIosMore onClick={() => handleMoreClick(rowIndex)} />
                      {visibleMenu === rowIndex && (
                        <OptionsMenu
                          menuItems={getMenuItems(row)}
                          visible={true}
                        />
                      )}
                    </td>
                    <td></td>
                    {Array.isArray(row.displayData) &&
                      row.displayData.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={headers.length + 1}
                    style={{ fontSize: "14px", borderBottom: "none" }}
                  >
                    <NoRecord />
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="paginationControls">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) =>
                  prev < Math.ceil(totalCount / pageSize) ? prev + 1 : prev
                )
              }
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
            >
              Next
            </button>
            {totalCount > 0 && (
              <div className="recordInfo">
                Showing {startRecord}–{endRecord} of {totalCount} results
              </div>
            )}
          </div>
        </div>
        <ToastContainer position="top-center" />
      </div>
    </div>
  );
};

export default FacultyList;
